import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Loader2, Coins, Wallet, Info } from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { api, formatApiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { useLang } from "../context/LangContext";

/**
 * Post-variant onboarding: captures the shop's initial gold weight and cash
 * once. Cannot be skipped — it's the base line used by سجل المحل to compute
 * the current balances. Editable later from Settings with a strong warning.
 */
export default function OpeningBalances() {
    const navigate = useNavigate();
    const { refresh } = useAuth();
    const { lang } = useLang();
    const [gold, setGold] = useState("");
    const [cash, setCash] = useState("");
    const [busy, setBusy] = useState(false);

    const submit = async () => {
        const g = parseFloat(gold);
        const c = parseFloat(cash);
        if (!Number.isFinite(g) || g < 0) { toast.error(lang === "ar" ? "أدخل وزن الذهب الابتدائي" : "Enter opening gold weight"); return; }
        if (!Number.isFinite(c) || c < 0) { toast.error(lang === "ar" ? "أدخل الكاش الابتدائي" : "Enter opening cash"); return; }
        setBusy(true);
        try {
            await api.post("/shop/opening-balances", { opening_gold_grams: g, opening_cash: c });
            if (refresh) await refresh();
            toast.success(lang === "ar" ? "تم حفظ الرصيد الابتدائي" : "Opening balances saved");
            // Go home — the ProtectedRoute chain will complete any remaining
            // onboarding steps (manager/employee-perms, etc.).
            navigate("/", { replace: true });
        } catch (e) {
            if (e.response?.status === 409) {
                toast.info(lang === "ar" ? "الرصيد الابتدائي محفوظ مسبقاً" : "Opening balances already saved");
                if (refresh) await refresh();
                navigate("/", { replace: true });
            } else toast.error(formatApiError(e));
        }
        finally { setBusy(false); }
    };

    return (
        <div className="min-h-screen app-bg" dir={lang === "ar" ? "rtl" : "ltr"}>
            <div className="mx-auto max-w-xl px-4 py-10 sm:px-6">
                <div className="mb-6 text-center">
                    <div className="inline-block rounded-full bg-amber-100 px-4 py-1 text-[11px] font-bold uppercase tracking-widest text-amber-900">
                        {lang === "ar" ? "خطوة أخيرة" : "Final step"}
                    </div>
                    <h1 className="mt-3 font-heading text-3xl font-bold text-neutral-900 sm:text-4xl">
                        {lang === "ar" ? "الرصيد الابتدائي للمحل" : "Shop opening balances"}
                    </h1>
                    <p className="mx-auto mt-2 max-w-md text-sm text-neutral-600">
                        {lang === "ar"
                            ? "أدخل بيانات البداية الآن — سيتم استخدامها كنقطة انطلاق لحسابات سجل المحل. يمكنك تعديلها لاحقاً من الإعدادات."
                            : "Enter your starting values now — they'll be the base for the Shop Log. Editable later from Settings."}
                    </p>
                </div>

                <div className="rounded-3xl border border-neutral-200 bg-white p-6 shadow-sm">
                    <div className="mb-3 flex items-start gap-2 rounded-xl bg-amber-50 p-3 text-xs text-amber-900 ring-1 ring-amber-200">
                        <Info className="mt-0.5 h-4 w-4 shrink-0" />
                        <div>
                            {lang === "ar"
                                ? "هذه القيم تُدخل مرة واحدة الآن. النظام سيبدأ منها في حساب الوضع الحالي لمحلك."
                                : "These values are captured once and used as the base for future calculations."}
                        </div>
                    </div>

                    <div className="space-y-4">
                        <div>
                            <Label className="mb-1 flex items-center gap-1 text-xs font-bold text-neutral-700">
                                <Coins className="h-3.5 w-3.5 text-amber-700" />
                                {lang === "ar" ? "وزن الذهب الكامل الآن (جرام)" : "Total gold weight now (grams)"}
                            </Label>
                            <Input
                                type="number" inputMode="decimal" step="0.001"
                                value={gold}
                                onChange={(e) => setGold(e.target.value)}
                                placeholder={lang === "ar" ? "مثال: 1250.500" : "e.g. 1250.500"}
                                data-testid="ob-gold-input"
                                className="h-11 font-numbers text-lg"
                            />
                        </div>
                        <div>
                            <Label className="mb-1 flex items-center gap-1 text-xs font-bold text-neutral-700">
                                <Wallet className="h-3.5 w-3.5 text-emerald-700" />
                                {lang === "ar" ? "الكاش الحالي في المحل" : "Cash on hand"}
                            </Label>
                            <Input
                                type="number" inputMode="decimal" step="0.001"
                                value={cash}
                                onChange={(e) => setCash(e.target.value)}
                                placeholder={lang === "ar" ? "مثال: 5000.000" : "e.g. 5000.000"}
                                data-testid="ob-cash-input"
                                className="h-11 font-numbers text-lg"
                            />
                        </div>
                    </div>

                    <div className="mt-6 flex items-center justify-end gap-2">
                        <Button
                            onClick={submit}
                            disabled={busy || !gold || !cash}
                            className="h-11 gap-2 bg-amber-700 px-6 text-white hover:bg-amber-800"
                            data-testid="ob-save-btn"
                        >
                            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
                            {lang === "ar" ? "حفظ ومتابعة" : "Save and continue"}
                        </Button>
                    </div>
                </div>
            </div>
        </div>
    );
}
